// === FILE HEADER: Chess Slider App Main Component ===
// This file implements the main SPA logic, UI orchestration, and event logging for the Chess Slider game.
// Structure and section anchors follow project documentation and copilot-instructions.md.
// All code, hooks, and logic are organized by explicit section headers for clarity and maintainability.
// Last updated: 2025-07-18 (Hooks refactored, shuffleMovesMax defined, conditional hooks removed)
//
// === END FILE HEADER ===
// === COPILOT FILE RULES ===
// DO NOT DELETE THE HEADER OR FILE RULES FROM THE FILE
// DO NOT PLACE ANY CODE, HOOKS, OR LOGIC ABOVE IMPORTS. 
// All import statements must be at the very top of the file, before any other code or comments (except for a file-level comment header or license block, if present).
// React hooks (useEffect, useState, etc.) must only be called inside a React function component or custom hook, never at the top level.
// Make a copy of the file in copies folder before doing any edits.
// Updating full large files has been difficult.  You may use the copy to refer to if it helps you.
// After completing edits compare the edited file to the original and make sure nothing other than the intended changes have happened
// If there are unintended changes repair the file
// After user approves the copy may be deleted
// DO NOT USE INLINE STYLES ALL STYLES MUST BE DEFINED IN THE PROPER FILE AND IMPORTED
// === END COPILOT FILE RULES ===

import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Board from './components/Board';
import SettingsPanel from './components/SettingsPanel';
import MoveHistoryDialog from './components/MoveHistoryDialog';
import useGameState from './hooks/useGameState';
import { playNoteSequence, playSound } from './utils/soundUtils';
import { isBoardSolved, getMovableTileIdsByRules, getInitialInPlaySquares, getInitialInPlayTiles, isValidChessCaptureMove, getEmptyChoices } from './utils/gameUtils';
import { logDomEvent, isLoggerPortEnabled, setLoggerTogglesLoaded } from './utils/spaLogger';


import modulePc from './App.pc.module.css';
import moduleMobile from './App.mobile.module.css';
import logo from './assets/images/chesSlider_logo_cropped_shrunk_again.png';

// === Section: Helper Functions ===
// Request fullscreen for mobile browsers
function requestFullscreen() {
  const el = document.documentElement;
  if (el.requestFullscreen) {
    el.requestFullscreen();
  } else if (el.webkitRequestFullscreen) {
    el.webkitRequestFullscreen();
  } else if (el.msRequestFullscreen) {
    el.msRequestFullscreen();
  }
}
// Animation duration helper for tile moves
function getAnimationDuration(tile, from, to) {
  if (!tile || !from || !to) return 300;
  if (tile.type === 'piece' && tile.pieceRef === 'knight') {
    return 500;
  }
  if (tile.type === 'piece' && (tile.pieceRef === 'bishop' || tile.pieceRef === 'queen')) {
    const fromFile = from[0].charCodeAt(0) - 97;
    const fromRank = parseInt(from[1], 10);
    const toFile = to[0].charCodeAt(0) - 97;
    const toRank = parseInt(to[1], 10);
    const dist = Math.max(Math.abs(fromFile - toFile), Math.abs(fromRank - toRank));
    return 200 + dist * 60;
  }
  return 300;
}

function formatElapsedTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// === Section: Main App Component ===
function App() {
// === All State and Hooks at Top ===
const boardRef = useRef(null);
const [loggerTogglesLoaded, setLoggerTogglesLoadedState] = useState(false);
const [elapsed, setElapsed] = useState(0);
const timerRef = useRef(null);
const [soundOn, setSoundOn] = useState(true);
const [volume, setVolume] = useState(0.5);
const [showSettingsPanel, setShowSettingsPanel] = useState(false);
const [resetTilesActive, setResetTilesActive] = useState(false);
const [shuffleClicked, setShuffleClicked] = useState(false);
const [pendingShuffleSnapshot, setPendingShuffleSnapshot] = useState(false);
const [lastShuffledState, setLastShuffledState] = useState(null);
const [solutionProgress, setSolutionProgress] = useState(0);
const [showVictoryDialog, setShowVictoryDialog] = useState(false);
const [victoryStats, setVictoryStats] = useState({ shuffled: 0, moves: 0, elapsed: '00:00' });
const [victoryDialogLocked, setVictoryDialogLocked] = useState(false);
const [lastMoveTime, setLastMoveTime] = useState(Date.now());
const [showStartupDialog, setShowStartupDialog] = useState(true);
const [playerName, setPlayerName] = useState('');
const [shuffleMoves, setShuffleMoves] = useState(50);
const [shuffleMovesError, setShuffleMovesError] = useState('');
const {
  inPlaySquares,
  inPlayTiles,
  emptySquare,
  moveHistory,
  settings,
  isShuffled,
  shuffleMode,
  setShuffleMode,
  handleMoveTile,
  handleRemoveTile,
  handleUndo,
  handleShuffle,
  handlePowerUp,
  handleSettingsChange,
  setInPlaySquares,
  setInPlayTiles,
  setEmptySquare,
  setMoveHistory,
  setIsShuffled,
  solveMovesActive,
  setSolveMovesActive,
  getSolveMoveTileId
} = useGameState();
const [selectedTile, setSelectedTile] = useState(null);
const [invalidMoveSquare, setInvalidMoveSquare] = useState(null);
const [tempMoveHints, setTempMoveHints] = useState(false);
const [captureHighlights, setCaptureHighlights] = useState([]);
const [awaitingTileRemoval, setAwaitingTileRemoval] = useState(false);
const [moveAnimation, setMoveAnimation] = useState(null);
const [movingTileId, setMovingTileId] = useState(null);
const shuffleBtnRef = useRef(null);
const inactivityTimerRef = useRef(null);
const prevShowStartupDialog = useRef(showStartupDialog);
const shuffleMovesMaxMap = { easy: 100, hard: 200, expert: 300 };
const shuffleMovesMax = shuffleMovesMaxMap[shuffleMode] || 100;
const showMoveHints = settings && settings.showMoveHints;
const gameReady = !!emptySquare && isShuffled;
const canRemoveTile = awaitingTileRemoval && !emptySquare && !isShuffled;
const canShuffleTiles = !isShuffled && !!emptySquare && !awaitingTileRemoval;
const canMoveTiles = isShuffled && !!emptySquare;

// === Style Switching State ===
  const [styles, setStyles] = useState(modulePc);

  // === Device Detection and Style Assignment ===
  useEffect(() => {
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    setStyles(isMobile ? moduleMobile : modulePc);
  }, []);

  // === All useEffect/useCallback hooks at top, never conditional ===
  useEffect(() => {
    try {
      const saved = localStorage.getItem('spaLoggerState');
      if (saved) {
        const parsed = JSON.parse(saved);
        window.__SPA_LOGGER_STATE__ = parsed;
      }
    } catch (e) { /* ignore */ }
    setLoggerTogglesLoadedState(true);
    setLoggerTogglesLoaded(true);
  }, []);

  useEffect(() => {
    const logPersistentUI = (selector, label) => {
      const elem = document.querySelector(selector);
      logDomEvent('App', 'ui-mounted', label, elem ? elem.outerHTML : '', elem, undefined);
    };
    logPersistentUI('.gameHeader', 'AppHeader');
    logPersistentUI('.gameStats', 'GameStats');
    logPersistentUI('.boardContainer', 'BoardContainer');
    logPersistentUI('.gameControls', 'GameControls');
  }, []);

  // === Instrumentation: Startup Dialog and Victory Dialog (logDomEvent version) ===
  const logStartupDialogEvent = async (action) => {
    // Logger gating is handled inside logDomEvent/isLoggerPortEnabled
    const overlayElem = document.querySelector(`.${styles.startupDialogOverlay}`);
    logDomEvent(
      'StartupDialog',
      action + '-overlay',
      'StartupDialogOverlay',
      overlayElem ? overlayElem.outerHTML : '',
      overlayElem,
      undefined
    );
    const dialogElem = document.querySelector(`.${styles.startupDialogBox}`);
    logDomEvent(
      'StartupDialog',
      action + '-box',
      'StartupDialogBox',
      dialogElem ? dialogElem.outerHTML : '',
      dialogElem,
      undefined
    );
  };

  const logVictoryDialogEvent = async (action) => {
    // Logger gating is handled inside logDomEvent/isLoggerPortEnabled
    const dialogElem = document.querySelector('.victoryDialog');
    logDomEvent(
      'VictoryDialog',
      action,
      '',
      dialogElem ? dialogElem.outerHTML : '',
      dialogElem,
      undefined
    );
  };

  // Log dialog entry on initial mount if open
  useEffect(() => {
    if (showStartupDialog) {
      logStartupDialogEvent('shown');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showStartupDialog && !prevShowStartupDialog.current) {
      logStartupDialogEvent('shown');
    } else if (!showStartupDialog && prevShowStartupDialog.current) {
      logStartupDialogEvent('hidden');
    }
    prevShowStartupDialog.current = showStartupDialog;
  }, [showStartupDialog, playerName, shuffleMode, shuffleMoves]);

  useEffect(() => {
    if (showVictoryDialog) {
      setTimeout(() => {
        logVictoryDialogEvent('shown');
      }, 0);
    } else {
      logVictoryDialogEvent('hidden');
    }
  }, [showVictoryDialog]);

  // === Board-Centered Dialog Positioning ===
  useEffect(() => {
    function updateBoardVars() {
      const board = document.querySelector('.boardContainer');
      if (board) {
        const boardRect = board.getBoundingClientRect();
        // Center dialog horizontally and anchor to top of board
        const boardCenterX = boardRect.left + boardRect.width / 2;
        const boardTopY = boardRect.top;
        document.documentElement.style.setProperty('--dialog-left', `${boardCenterX}px`);
        document.documentElement.style.setProperty('--dialog-top', `${boardTopY}px`);
        document.documentElement.style.setProperty('--board-left', `${boardRect.left}px`);
        document.documentElement.style.setProperty('--board-square-size', `${boardRect.width / 8}px`);
        // Do NOT add boardReady class here
      }
      const stats = document.querySelector('.gameStats');
      if (stats) {
        const statsRect = stats.getBoundingClientRect();
        document.documentElement.style.setProperty('--game-stats-bottom', `${statsRect.bottom}px`);
      }
    }
    updateBoardVars();
    window.addEventListener('resize', updateBoardVars);
    return () => {
      window.removeEventListener('resize', updateBoardVars);
    };
  }, []);

  // === Add modal-active class to root when Startup Dialog is open ===
  useEffect(() => {
    if (showStartupDialog) {
      document.documentElement.classList.add('modal-active');
    } else {
      document.documentElement.classList.remove('modal-active');
    }
  }, [showStartupDialog]);

  // === Add boardReady class only after dialog is closed ===
  useEffect(() => {
    if (!showStartupDialog) {
      document.documentElement.classList.add('boardReady');
    } else {
      document.documentElement.classList.remove('boardReady');
    }
  }, [showStartupDialog]);

  useEffect(() => {
    if (isShuffled && !showVictoryDialog) {
      // Start timer
      if (!timerRef.current) {
        timerRef.current = setInterval(() => {
          setElapsed(prev => prev + 1);
        }, 1000);
      }
    } else {
      // Stop timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      // Reset timer if not shuffled
      if (!isShuffled) {
        setElapsed(0);
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isShuffled, showVictoryDialog]);
  // Moves and move hints are only enabled after the board is shuffled
  // Remove Tile is enabled if awaitingTileRemoval and emptySquare is null and isShuffled is false
  // Shuffle Tiles is enabled if !isShuffled and emptySquare is set and not awaitingTileRemoval
  // Moves are enabled only if isShuffled is true and emptySquare is set
  // Remove unused setLineOfSightEnabled for now
  // const [lineOfSightEnabled, setLineOfSightEnabled] = useState(true); // toggle for line of sight

  // === UI/UX: Only allow Remove Tile until emptySquare is set ===
  // (Handled above with canRemoveTile, canShuffleTiles, canMoveTiles)

  // === Remove Tile Logic ===
  // Returns all color tile IDs currently present on the board (i.e., still in inPlayTiles)
  const getColorTileIdsOnBoard = () => {
    const ids = Object.values(inPlayTiles || {})
      .filter(tile => tile.type === 'color')
      .map(tile => tile.tileId);
    return ids;
  };
  const removeRandomColorTile = () => {
    let colorTileIds = getColorTileIdsOnBoard();
    if (shuffleMode === 'easy') {
      // Only allow eligible tiles (not in files 'a' or 'h')
      const eligibleSquares = getEmptyChoices('easy');
      colorTileIds = colorTileIds.filter(tileId => {
        const tile = inPlayTiles[tileId];
        return eligibleSquares.includes(tile.fileRank);
      });
    }
    if (colorTileIds.length === 0) {
      return;
    }
    const randomId = colorTileIds[Math.floor(Math.random() * colorTileIds.length)];
    handleRemoveTile(randomId);
    setAwaitingTileRemoval(false);
  };

  // === Board Click Handler (Refactored for Sound Queue and Animation Sync) ===

  const handleSquareClick = (squareId) => {
    const squareObj = inPlaySquares[squareId];
    if (!squareObj) return;
    const tileId = squareObj.tileId;
    // === Remove Tile Phase ===
    if (canRemoveTile && tileId) {
      const tile = inPlayTiles[tileId];
      if (!tile) return;
      if (tile.type === 'color') {
        // Enforce easy mode restriction: only allow interior color tiles
        if (shuffleMode === 'easy') {
          const eligible = getEmptyChoices('easy');
          if (!eligible.includes(squareId)) {
            if (soundOn) playSound('blocked', volume);
            setInvalidMoveSquare(squareId);
            setTimeout(() => setInvalidMoveSquare(null), 350);
            return;
          }
        }
        if (soundOn) {
          playNoteSequence(tile.note, volume, () => playSound('moved', volume));
        }
        handleRemoveTile(tile.tileId);
        setAwaitingTileRemoval(false);
        return;
      } else {
        if (soundOn) {
          playNoteSequence(tile.note, volume, () => playSound('blocked', volume));
        }
      }
      return;
    }
    // === Block all moves until board is shuffled ===
    if (!canMoveTiles) {
      if (tileId) {
        if (soundOn) playSound('blocked', volume);
        setInvalidMoveSquare(squareId);
        setTimeout(() => setInvalidMoveSquare(null), 350);
      }
      return;
    }
    // === Normal Move Phase (after shuffle) ===
    if (tileId && handleMoveTile) {
      const tile = inPlayTiles[tileId];
      const isMovable = handleMoveTile(tileId, true); // dryRun
      if (isMovable) {
        // Step 1: Play note and shimmer green immediately
        if (soundOn && tile.note) {
          playNoteSequence(tile.note, volume);
        }
        setMovingTileId(tileId); // Step 2: Green shimmer on moving tile
        setMoveAnimation({ tileId, from: tile.fileRank, to: emptySquare });
        const animDuration = getAnimationDuration(tile, tile.fileRank, emptySquare);
        setTimeout(() => {
          handleMoveTile(tileId);
          setMoveAnimation(null);
          setMovingTileId(null); // Step 3: Remove shimmer
          if (soundOn) playSound('moved', volume); // Step 3: Play moved sound
          setLastMoveTime(Date.now());
          setTempMoveHints(false);
        }, animDuration);
      } else {
        if (soundOn) {
          playSound('blocked', volume);
        }
        setInvalidMoveSquare(squareId);
        setTimeout(() => setInvalidMoveSquare(null), 350);
        return;
      }
    } else {
      // Handle capture mode: highlight legal capture squares for chess pieces
      if (tileId) {
        const tile = inPlayTiles[tileId];
        if (tile && tile.type === 'piece') {
          const highlights = Object.keys(inPlaySquares).filter(destId => {
            if (destId === squareId) return false;
            return isValidChessCaptureMove(tile, squareId, destId, inPlaySquares, true);
          });
          setCaptureHighlights(highlights);
          setSelectedTile(tileId);
          return;
        } else if (tile && tile.type === 'color') {
          if (soundOn) playSound('blocked', volume);
          setInvalidMoveSquare(squareId);
          setTimeout(() => setInvalidMoveSquare(null), 350);
          setCaptureHighlights([]);
          setSelectedTile(null);
          return;
        }
      }
    }
    setCaptureHighlights([]);
    setSelectedTile(null);
  };

  // === Shuffle/Reset Handlers ===
  const handleShuffleTiles = () => {
    if (!canShuffleTiles) return;
    handleShuffle(shuffleMoves);
    playGameSound('shuffle');
    setResetTilesActive(true);
    setShuffleClicked(true);
    setPendingShuffleSnapshot(true);
  };
  // === Take snapshot for Reset Tiles only after shuffle is complete ===
  useEffect(() => {
    if (pendingShuffleSnapshot && isShuffled) {
      setLastShuffledState({
        inPlaySquares: { ...inPlaySquares },
        inPlayTiles: { ...inPlayTiles },
        emptySquare,
        moveHistory: [...moveHistory]
      });
      setPendingShuffleSnapshot(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingShuffleSnapshot, isShuffled, inPlaySquares, inPlayTiles, emptySquare, moveHistory]);

  const handleResetTiles = () => {
    if (!lastShuffledState) return;
    setInPlaySquares({ ...lastShuffledState.inPlaySquares });
    setInPlayTiles({ ...lastShuffledState.inPlayTiles });
    setEmptySquare(lastShuffledState.emptySquare);
    setMoveHistory([]);
    setElapsed(0); // Reset elapsed timer to 0 on Reset
    setResetTilesActive(true); // Keep shimmer after reset so Reset can be used multiple times
    setShuffleClicked(false); // Re-enable Shuffle Tiles after reset
  };

  // === Startup Dialog: Start Game ===
  // Start a new game: reset all state to defaults and close dialogs
const handleStartGame = () => {
  // Try to get the startup dialog DOM element for UI info
  let ui = undefined;
  const dialogElem = document.querySelector(`.${styles.startupDialogBox}`);
  if (dialogElem) {
    const rect = dialogElem.getBoundingClientRect();
    const computed = window.getComputedStyle(dialogElem);
    ui = {
      width: rect.width,
      height: rect.height,
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
      color: computed.color,
      backgroundColor: computed.backgroundColor
    };
  }
  // Startup dialog logging now handled by logDomEvent
  setAwaitingTileRemoval(true);
  setEmptySquare(null);
  setInPlaySquares(getInitialInPlaySquares());
  setInPlayTiles(getInitialInPlayTiles());
  setMoveHistory([]);
  setIsShuffled(false);
  setShuffleClicked(false);
  setResetTilesActive(false);
  setShowVictoryDialog(false);
  setVictoryStats({ shuffled: 0, moves: 0, elapsed: '00:00' });
  setSolutionProgress(0);
  setElapsed(0);
  setLastShuffledState(null);
  setPendingShuffleSnapshot(false);
  setSelectedTile(null);
  setInvalidMoveSquare(null);
  setTempMoveHints(false);
  setCaptureHighlights([]);
  setMoveAnimation(null);
  setMovingTileId(null);
  setLastMoveTime(Date.now());
  setVictoryDialogLocked(false);
  // Player name, color, shuffle mode, shuffle moves are preserved from dialog

  // === Board Refresh on Start Game ===
  if (boardRef.current && boardRef.current.forceBoardResize) {
    boardRef.current.forceBoardResize();
  }
  // Request fullscreen on mobile when starting game (production only)
  if (
    process.env.NODE_ENV === 'production' &&
    /Mobi|Android/i.test(navigator.userAgent)
  ) {
    requestFullscreen();
  }
  setShowStartupDialog(false);
};

  // === Sound Utility Wrapper ===
  const playGameSound = useCallback((type) => {
    if (soundOn) playSound(type, volume);
  }, [soundOn, volume]);

  // === Solved State Check ===
useEffect(() => {
  // Only check victory if the board has been shuffled and inPlayTiles has changed
  if (isShuffled && !victoryDialogLocked) {
    const solved = isBoardSolved(inPlayTiles, inPlaySquares, emptySquare);
    setSolutionProgress(solved ? 100 : 0);
    // Log detailed solved state and gating conditions
    Object.values(inPlayTiles).forEach(t => {
    });
    // Only show victory dialog if the board has been shuffled and is solved
    if (solved && !showVictoryDialog) {
      // Capture stats at victory
      setVictoryStats({
        shuffled: settings && settings.shuffleMoves ? settings.shuffleMoves : shuffleMoves,
        moves: moveHistory ? moveHistory.length : 0,
        elapsed: isShuffled ? formatElapsedTime(elapsed) : '00:00'
      });
      setShowVictoryDialog(true);
      setVictoryDialogLocked(true);
      playGameSound('victory'); // Play victory sound
    }
  }
}, [inPlayTiles, isShuffled, emptySquare, inPlaySquares, playGameSound, showVictoryDialog, settings, shuffleMoves, moveHistory, elapsed, victoryDialogLocked]);

  // === Ref for Shuffle Tiles button to set checkerboard size ===
  useEffect(() => {
    function updateCheckerSize() {
      if (shuffleBtnRef.current) {
        const height = shuffleBtnRef.current.offsetHeight;
        const checkerSize = height / 2;
        shuffleBtnRef.current.style.setProperty('--checker-size', checkerSize + 'px');
      }
    }
    updateCheckerSize();
    window.addEventListener('resize', updateCheckerSize);
    return () => window.removeEventListener('resize', updateCheckerSize);
  }, []);

  // === Move Hints and Inactivity Timer Logic ===
  useEffect(() => {
    // Move hints and inactivity timer logic
    let timer = null;
    if (solveMovesActive) {
      const solveTileId = getSolveMoveTileId();
      if (solveTileId) {
        setCaptureHighlights([solveTileId]);
      } else {
        setCaptureHighlights([]);
      }
    } else if (!canMoveTiles) {
      setCaptureHighlights([]);
    } else if (showMoveHints) {
      const movableIds = inPlayTiles && inPlaySquares && emptySquare
        ? getMovableTileIdsByRules(inPlaySquares, inPlayTiles, emptySquare)
        : [];
      setCaptureHighlights(movableIds);
    } else {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      timer = setTimeout(() => {
        if (canMoveTiles) {
          const movableIds = inPlayTiles && inPlaySquares && emptySquare
            ? getMovableTileIdsByRules(inPlaySquares, inPlayTiles, emptySquare)
            : [];
          setCaptureHighlights(movableIds);
        }
      }, 5000);
      inactivityTimerRef.current = timer;
    }
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [inPlayTiles, inPlaySquares, emptySquare, lastMoveTime, isShuffled, showMoveHints, canMoveTiles, solveMovesActive, getSolveMoveTileId]);

  // Remove highlight when a move is made, unless Show Move Hints is enabled or Solve Moves is active
  useEffect(() => {
    if (!(showMoveHints || solveMovesActive) && lastMoveTime && captureHighlights.length > 0) {
      setCaptureHighlights([]);
    }
  }, [lastMoveTime, captureHighlights.length, showMoveHints, solveMovesActive]);

  // === UI: Add Remove Tile button with flashing border ===
  // Define modalRoot immediately before return for correct scope
  const modalRoot = typeof document !== 'undefined' ? document.getElementById('modal-root') : null;
  // === Portal Root Diagnostic ===
  useEffect(() => {
    if (typeof document !== 'undefined' && !document.getElementById('modal-root')) {
      // eslint-disable-next-line no-console
      console.warn('WARNING: modal-root portal element is missing from the DOM. Startup Dialog portal will not render.');
    }
  }, []);

  // === DEBUG: Log styles object to verify CSS module keys ===
  if (typeof window !== 'undefined') {
    // Only log once per reload
    if (!window.__LOGGED_STYLES_ONCE__) {
      window.__LOGGED_STYLES_ONCE__ = true;
      // eslint-disable-next-line no-console
      console.log('DEBUG: styles object at runtime:', styles);
    }
  }

  return (
    loggerTogglesLoaded ? (
      <>
        <div className={styles.App}>
          <main className={styles.mainGameArea}>
            {/* === Header === */}
            <header className={styles.gameHeader}>
              <div className={styles.gameHeaderRow}>
                <div className={styles.gameTitle}>
                  <img
                    src={logo}
                    alt="Chess Slider Logo"
                  />
                </div>
                <div className={styles.playerName}>Player: {playerName}</div>
                <div className={styles.gameSettings}>
                  <button
                    className={styles.settingsBtn}
                    aria-label="Settings"
                    title="Settings"
                    onClick={() => setShowSettingsPanel(true)}
                  >
                    ⚙️
                  </button>
                  {/* === Settings Panel Overlay (anchored) === */}
                  {showSettingsPanel && (
                    <SettingsPanel
                      settings={{
                        ...settings,
                        moveHistory,
                        elapsed
                      }}
                      handleSettingsChange={handleSettingsChange}
                      setShowSettingsPanel={setShowSettingsPanel}
                      resetGame={async () => {
                        handleStartGame();
                        setShowStartupDialog(true); // Restore: re-show Startup Dialog after reset
                      }}
                      soundOn={soundOn}
                      setSoundOn={setSoundOn}
                      volume={volume}
                      setVolume={setVolume}
                      anchorRight
                      setSolveMovesActive={setSolveMovesActive}
                      solveMovesActive={solveMovesActive}
                      width={`calc(var(--board-square-size, 100px) * 4)}`}
                      loggerTogglesLoaded={loggerTogglesLoaded}
                    />
                  )}
                </div>
              </div>
            </header>
            {/* === Game Stats === */}
            <div className={styles.gameStats}>
              <div className={styles.gameStatsRow}>
                <div className={styles.statRow}>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Shuffled:</span>
                    <span className={styles.statValue}>{settings && settings.shuffleMoves ? settings.shuffleMoves : shuffleMoves}</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Moves:</span>
                    <span className={styles.statValue}>{moveHistory ? moveHistory.length : 0}</span>
                  </div>
                  <div className={styles.statItem}>
                    <span className={styles.statLabel}>Elapsed:</span>
                    <span className={styles.statValue}>
                      {isShuffled ? formatElapsedTime(elapsed) : '00:00'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* === Board and Portal-Based Overlay (handled by Board.js) === */}
            <Board
              ref={boardRef}
              inPlaySquares={inPlaySquares}
              inPlayTiles={inPlayTiles}
              invalidMoveSquare={invalidMoveSquare}
              handleSquareClick={handleSquareClick}
              captureHighlights={captureHighlights}
              showFileRank={settings && settings.showFileRank}
              awaitingTileRemoval={awaitingTileRemoval}
              emptySquare={emptySquare}
              shuffleMode={shuffleMode}
              boardRect={null}
              squareSize={null}
              solveMovesActive={solveMovesActive}
              showMoveHints={showMoveHints}
              getSolveMoveTileId={getSolveMoveTileId}
              showSolvedIndicator={settings && settings.showSolvedIndicator}
            />
            {showVictoryDialog && (
              <div className={styles.victoryOverlay}>
                <div className={`${styles.victoryDialog} ${styles.victoryDialogBoardSized}`}>
                  <div className={`${styles.victoryEmoji} ${styles.victoryEmojiBoardSized}`}>🎆</div>
                  <h2 className={styles.victoryTitle}>Congratulations, {playerName}!</h2>
                  <p className={styles.victoryText}>You solved the chesSlider puzzle!</p>
                  <p className={styles.victoryStats}>
                    <span><strong>Shuffled:</strong> {victoryStats.shuffled}</span><br />
                    <span><strong>Moves:</strong> {victoryStats.moves}</span><br />
                    <span><strong>Elapsed:</strong> {victoryStats.elapsed}</span>
                  </p>
                  <div className={styles.victoryButtons}>
                    <button
                      className={`${styles.victoryButton} ${styles.continue}`}
                      onFocus={e => isLoggerPortEnabled('VictoryDialog') && logDomEvent('VictoryDialog', 'button-focus', 'OK Button', e.target.outerHTML, e.target, undefined)}
                      onClick={async e => {
                        if (isLoggerPortEnabled('VictoryDialog')) await logDomEvent('VictoryDialog', 'ok-button-clicked', 'OK Button', e.target.outerHTML, e.target, undefined);
                        await logVictoryDialogEvent('hidden');
                        setVictoryDialogLocked(false);
                        setShowVictoryDialog(false);
                        setInPlaySquares(getInitialInPlaySquares());
                        setInPlayTiles(getInitialInPlayTiles());
                        setEmptySquare(null);
                        setMoveHistory([]);
                        setSolutionProgress(0);
                        setResetTilesActive(false);
                        setShuffleClicked(false);
                        setAwaitingTileRemoval(true);
                        setElapsed(0); // Reset timer on new game
                        if (typeof setIsShuffled === 'function') setIsShuffled(false);
                      }}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
            {/* === Game Controls === */}
            <div className={styles.gameControls}>
              <div className={styles.gameControlsRow}>
                {/* === Section: Remove Tile Button (Left) === */}
                <div className={styles.gameControlsBtnCol}>
                  <button
                    className={`${styles.removeTileBtn} ${styles.controlBtn} ${canRemoveTile ? styles.shimmerTileBtn : styles.btnInactive}`}
                    onClick={async (e) => {
                      logDomEvent('GameControls', 'button-click', 'Remove', e.target.outerHTML, e.target, undefined);
                      if (canRemoveTile) {
                        removeRandomColorTile();
                      }
                    }}
                    onFocus={e => isLoggerPortEnabled('GameControls') && logDomEvent('GameControls', 'button-focus', 'Remove', e.target.outerHTML, e.target, undefined)}
                    onBlur={e => isLoggerPortEnabled('GameControls') && logDomEvent('GameControls', 'button-blur', 'Remove', e.target.outerHTML, e.target, undefined)}
                    disabled={!canRemoveTile}
                  >
                    REMOVE
                  </button>
                </div>
                <div className={styles.gameControlsSpacerCol}></div>
                {/* === Section: Shuffle Tiles Button (Center) === */}
                <div className={styles.gameControlsBtnCol}>
                  <button
                    className={`${styles.shuffleTilesBtn} ${styles.controlBtn} ${(canShuffleTiles && !shuffleClicked) ? styles.shimmerTileBtn : styles.btnInactive}`}
                    onClick={async (e) => {
                      logDomEvent('GameControls', 'button-click', 'Shuffle', e.target.outerHTML, e.target, undefined);
                      handleShuffleTiles();
                    }}
                    onFocus={e => isLoggerPortEnabled('GameControls') && logDomEvent('GameControls', 'button-focus', 'Shuffle', e.target.outerHTML, e.target, undefined)}
                    onBlur={e => isLoggerPortEnabled('GameControls') && logDomEvent('GameControls', 'button-blur', 'Shuffle', e.target.outerHTML, e.target, undefined)}
                    disabled={!canShuffleTiles || shuffleClicked}
                  >
                    SHUFFLE
                  </button>
                </div>
                <div className={styles.gameControlsSpacerCol}></div>
                {/* === Section: Reset Tiles Button (Right) === */}
                <div className={styles.gameControlsBtnCol}>
                  <button
                    className={`${styles.resetTilesBtn} ${styles.controlBtn} ${resetTilesActive ? styles.shimmerTileBtn : styles.btnInactive}`}
                    onClick={async (e) => {
                      logDomEvent('GameControls', 'button-click', 'Reset', e.target.outerHTML, e.target, undefined);
                      handleResetTiles();
                    }}
                    onFocus={e => isLoggerPortEnabled('GameControls') && logDomEvent('GameControls', 'button-focus', 'Reset', e.target.outerHTML, e.target, undefined)}
                    onBlur={e => isLoggerPortEnabled('GameControls') && logDomEvent('GameControls', 'button-blur', 'Reset', e.target.outerHTML, e.target, undefined)}
                    disabled={!lastShuffledState || !gameReady}
                  >
                    RESET
                  </button>
                </div>
              </div>
            </div>
            {/* Move History Dialog (controlled by settings) */}
            {settings && moveHistory && settings.showMoveHistory ? (
              (() => {
                const moveHistoryStrings = moveHistory.map((move, idx) => {
                  const moveNum = idx + 1;
                  return `#${moveNum}: ${move.tileId} ${move.from} → ${move.to}`;
                });
                const dialogWidth = `calc(var(--board-square-size, 100px) * 2.5)`;
                return (
                  <MoveHistoryDialog
                    moves={moveHistoryStrings}
                    onClose={() => handleSettingsChange({ showMoveHistory: false })}
                    width={dialogWidth}
                    loggerTogglesLoaded={loggerTogglesLoaded}
                  />
                );
              })()
            ) : null}
          </main>
        </div>
        {/* Startup Dialog Portal must be inside the fragment but outside main game area */}
        {showStartupDialog && modalRoot && ReactDOM.createPortal(
          <div className={styles.startupDialogOverlay}>
            <div className={`${styles.startupDialogBox} ${styles.noTopPad}`}>
              <img src={logo} alt="Chess Slider Logo" className={styles.startupLogoEdgeToEdge} />
              <form
                className={styles.startupDialogForm}
                onSubmit={e => {
                  e.preventDefault();
                  if (shuffleMoves > shuffleMovesMax) {
                    setShuffleMovesError(`Max for ${shuffleMode} is ${shuffleMovesMax}`);
                    return;
                  }
                  setShuffleMovesError('');
                  const btn = e.target.querySelector('button[type="submit"]');
                  logDomEvent('StartupDialog', 'form-submit', 'Start Game Submit', btn.outerHTML, btn, undefined);
                  logStartupDialogEvent('hidden');
                  handleStartGame();
                }}
              >
                <div className={styles.formRow}>
                  <label htmlFor="playerName" style={{ display: 'block', textAlign: 'left' }}>Player Name:</label>
                  <input
                    id="playerName"
                    type="text"
                    value={playerName}
                    onFocus={e => logDomEvent('StartupDialog', 'input-focus', 'Player Name', e.target.outerHTML, e.target, undefined)}
                    onChange={e => {
                      setPlayerName(e.target.value);
                      logDomEvent('StartupDialog', 'input-change', 'Player Name', e.target.outerHTML, e.target, undefined);
                    }}
                  />
                </div>
                <div className={styles.formRow}>
                  <label htmlFor="shuffleMode" style={{ display: 'block', textAlign: 'left' }}>Shuffle Mode:</label>
                  <select
                    id="shuffleMode"
                    value={shuffleMode}
                    onFocus={e => logDomEvent('StartupDialog', 'input-focus', 'Shuffle Mode', e.target.outerHTML, e.target, undefined)}
                    onChange={e => {
                      setShuffleMode(e.target.value);
                      logDomEvent('StartupDialog', 'input-change', 'Shuffle Mode', e.target.outerHTML, e.target, undefined);
                    }}
                  >
                    <option value="easy">Easy</option>
                    <option value="hard">Hard</option>
                    <option value="expert">Expert</option>
                  </select>
                </div>
                <div className={styles.formRow}>
                  <label htmlFor="shuffleMoves" style={{ display: 'block', textAlign: 'left' }}>Shuffle Moves:</label>
                  <input
                    id="shuffleMoves"
                    type="number"
                    min={1}
                    max={shuffleMovesMax}
                    value={shuffleMoves}
                    onFocus={e => logDomEvent('StartupDialog', 'input-focus', 'Shuffle Moves', e.target.outerHTML, e.target, undefined)}
                    onChange={e => {
                      const val = Number(e.target.value);
                      if (val > shuffleMovesMax) {
                        setShuffleMovesError(`Max for ${shuffleMode} is ${shuffleMovesMax}`);
                        setShuffleMoves(shuffleMovesMax);
                      } else {
                        setShuffleMovesError('');
                        setShuffleMoves(val);
                      }
                      logDomEvent('StartupDialog', 'input-change', 'Shuffle Moves', e.target.outerHTML, e.target, undefined);
                    }}
                  />
                  {shuffleMovesError && (
                    <div style={{ color: 'red', fontSize: 13, marginTop: 2 }}>{shuffleMovesError}</div>
                  )}
                </div>
                <div className={styles.formRowFull}>
                  <button
                    type="submit"
                    onFocus={e => logDomEvent('StartupDialog', 'button-focus', 'Start Game Button', e.target.outerHTML, e.target, undefined)}
                    onClick={e => logDomEvent('StartupDialog', 'button-click', 'Start Game Button', e.target.outerHTML, e.target, undefined)}
                  >
                    Start Game
                  </button>
                </div>
              </form>
            </div>
          </div>,
          modalRoot
        )}
      </>
    ) : (
      <div className={styles.appLoading}>Loading...</div>
    )
  );
}

export default App;

